import { type QuestionnaireStatus } from "@app/screens/ExternalQuestionnaires/types";
import { UserMultiSelect } from "@app/shared/components/UserMultiSelect";
import { Dropdown } from "@design-system/Inputs/UniversalDropdown";
import { FlexRow } from "@design-system/Layout/Flex";
import { Text } from "@design-system/Typography/Text";
import {
    QuestionType,
    type AiAnswerStatusFilter,
    type QuestionStatusFilter,
} from "@generated/client/graphql";
import {
    type Dispatch,
    type FC,
    type PropsWithChildren,
    type SetStateAction,
} from "react";
import { questionTypeToIconName } from "../OverviewTab/components/QuestionsTable/columns/answerCol";
import { QuestionStatusMultiSelect } from "../QuestionsTab/components/QuestionStatusMultiSelect";
import { useExternalQuestionnaireFilterChipsTranslations } from "./i18n";

export const AssigneeFilterChip: FC<{
    value: (string | null)[];
    onValueChange: (val: (string | null)[]) => void;
    searchFilter?: string;
    onSearchFilterChange?: Dispatch<SetStateAction<string | undefined>>;
    includeNone?: boolean;
}> = ({
    value,
    onValueChange,
    searchFilter,
    onSearchFilterChange,
    includeNone,
}) => {
    const { t, keys } = useExternalQuestionnaireFilterChipsTranslations();
    return (
        <UserMultiSelect
            filterLabel={t(keys.assignee_label)}
            value={value}
            onValueChange={onValueChange}
            searchFilter={searchFilter}
            onSearchFilterChange={onSearchFilterChange}
            isFilter
            includeNone={includeNone}
        />
    );
};

export const QuestionStatusFilterChip: FC<{
    value: QuestionStatusFilter[];
    onValueChange: (value: QuestionStatusFilter[]) => void;
}> = ({ value, onValueChange }) => {
    const { t, keys } = useExternalQuestionnaireFilterChipsTranslations();
    return (
        <QuestionStatusMultiSelect
            value={value}
            onValueChange={onValueChange}
            statusType="question"
            triggerLabel={t(keys.question_status_filter)}
            isFilter
        />
    );
};

export const AiAnswerStatusFilterChip: FC<{
    value: AiAnswerStatusFilter[];
    onValueChange: (value: AiAnswerStatusFilter[]) => void;
}> = ({ value, onValueChange }) => {
    const { t, keys } = useExternalQuestionnaireFilterChipsTranslations();
    return (
        <QuestionStatusMultiSelect
            value={value}
            onValueChange={onValueChange}
            statusType="aiAnswer"
            triggerLabel={t(keys.ai_answer_status_filter)}
            isFilter
        />
    );
};

export const QuestionnaireStatusFilterChip: FC<{
    value: QuestionnaireStatus[];
    onValueChange: (value: QuestionnaireStatus[]) => void;
}> = ({ value, onValueChange }) => {
    const { t, keys } = useExternalQuestionnaireFilterChipsTranslations();
    return (
        <QuestionStatusMultiSelect
            value={value}
            onValueChange={onValueChange}
            statusType="questionnaire"
            triggerLabel={t(keys.questionnaire_status_filter)}
            isFilter
        />
    );
};

export const AnswerTypeFilterChip: FC<{
    value: QuestionType[];
    onValueChange: (value: QuestionType[]) => void;
}> = ({ value, onValueChange }) => {
    const { t, keys } = useExternalQuestionnaireFilterChipsTranslations();
    const options = Object.values(QuestionType).map((questionType) => ({
        value: questionType,
        label: t(getQuestionTypeLabel(questionType)),
        icon: questionTypeToIconName[questionType],
    }));

    const handleTypeChange = (values: (string | null)[]) => {
        onValueChange(values.filter((id) => id !== null) as QuestionType[]);
    };

    return (
        <Dropdown
            closeOnValueChange={false}
            value={value || []}
            onValueChange={handleTypeChange}
            selectType="multi"
        >
            <Dropdown.ChipFilterTrigger
                label={t(keys.answer_type_filter)}
                options={options}
                valueIdentifierKey="value"
                valueDisplayKey="label"
            />

            <Dropdown.Content align="start" hasPortal>
                {options?.map((option) => (
                    <Dropdown.SelectableItem
                        key={option.value}
                        value={option.value}
                    >
                        <Dropdown.Checkbox />
                        <Dropdown.ItemIcon
                            name={option.icon}
                            className="!text-primary"
                        />
                        <Dropdown.ItemText>{t(option.label)}</Dropdown.ItemText>
                    </Dropdown.SelectableItem>
                ))}
            </Dropdown.Content>
        </Dropdown>
    );
};

export const FilterChips: FC<PropsWithChildren> & {
    AssigneeFilterChip: typeof AssigneeFilterChip;
    QuestionStatusFilterChip: typeof QuestionStatusFilterChip;
    AiAnswerStatusFilterChip: typeof AiAnswerStatusFilterChip;
    QuestionnaireStatusFilterChip: typeof QuestionnaireStatusFilterChip;
    AnswerTypeFilterChip: typeof AnswerTypeFilterChip;
} = ({ children }) => {
    const { t, keys } = useExternalQuestionnaireFilterChipsTranslations();
    return (
        <FlexRow wrap="wrap" gap="2" w="full" alignItems="center">
            <Text variant="caption-bold">{t(keys.label)}</Text>
            {children}
        </FlexRow>
    );
};

FilterChips.AssigneeFilterChip = AssigneeFilterChip;
FilterChips.QuestionStatusFilterChip = QuestionStatusFilterChip;
FilterChips.AiAnswerStatusFilterChip = AiAnswerStatusFilterChip;
FilterChips.QuestionnaireStatusFilterChip = QuestionnaireStatusFilterChip;
FilterChips.AnswerTypeFilterChip = AnswerTypeFilterChip;

function getQuestionTypeLabel(questionType: QuestionType): string {
    if (questionType === QuestionType.SingleSelect) {
        return "question_type_single_select";
    }
    if (questionType === QuestionType.MultiSelect) {
        return "question_type_multi_select";
    }
    if (questionType === QuestionType.Text) {
        return "question_type_text";
    }
    return "question_type_number";
}
